import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FanRequestsPanel, { FanLanding } from "../components/FanRequestsPanel";
import FanProfileEditorSheet from "../components/FanProfileEditorSheet";
import FanSignInSheet from "../components/FanSignInSheet";
import { isFanSignedIn } from "../lib/fanAccount";
import { useScrollLockHeal } from "../hooks/useScrollLockHeal";
import "./FanRequestsPage.css";

// 🎟️ My Requests — the fan's home base (founder pick 1A). Where
// a fan lands the moment they pay: /fan/requests?order=<id> opens that
// request as the live card at the top of tonight's block. The old /success
// and /free-success URLs redirect here with their params intact, so every
// email, push and Stripe return link keeps working.
//
// URL contract (parsed once):
//   order       the request to open (legacy: order_id)
//   session_id  Stripe Checkout return (cs_…)
//   redirect_status / payment_intent / payment_intent_client_secret
//               Stripe redirect-based confirmation (3DS etc.)
// "My Profile ›" opens the same editor / sign-in sheets the header chip uses.

const ORDER_ID_RE = /^[a-f0-9]{24}$/i;

const FanRequestsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // A stranded sheet scroll-lock makes the page unscrollable — heal on
  // arrival AND on iOS bfcache restore (the confirmation page needed this).
  useScrollLockHeal();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorVerify, setEditorVerify] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  const landing = useMemo<FanLanding | null>(() => {
    const rawOrder = searchParams.get("order") || searchParams.get("order_id");
    const rawSession = searchParams.get("session_id");
    const orderId = rawOrder && ORDER_ID_RE.test(rawOrder) ? rawOrder : null;
    const sessionId = rawSession && /^cs_[A-Za-z0-9_]+$/.test(rawSession) ? rawSession : null;
    if (!orderId && !sessionId) return null;
    return {
      orderId,
      sessionId,
      redirectStatus: searchParams.get("redirect_status"),
      piId: searchParams.get("payment_intent"),
      piSecret: searchParams.get("payment_intent_client_secret"),
    };
    // Parse once per arrival — the panel treats it as the landing event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditor = () => {
    // Identity = account-only (founder decision): signed-out fans have nothing
    // to edit — "My Profile ›" IS the sign-up door for them.
    if (isFanSignedIn()) setEditorOpen(true);
    else setSignInOpen(true);
  };

  return (
    // Scroll lives inside the tab (same recipe as the leaderboard tab):
    // document never scrolls → iOS rubber-band can't drag the fixed bars.
    <div className="fanreq-scroll">
      <div className="fanreq-page">
        <FanRequestsPanel
          active
          frame="page"
          landing={landing}
          onOpenEditor={openEditor}
          onVerifyInstagram={() => {
            setEditorVerify(true);
            setEditorOpen(true);
          }}
        />
        <FanProfileEditorSheet
          open={editorOpen}
          connectOnOpen={editorVerify}
          onClose={() => {
            setEditorOpen(false);
            setEditorVerify(false);
          }}
        />
        <FanSignInSheet open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    </div>
  );
};

export default FanRequestsPage;
